/**
 * FilterPanel Component
 *
 * Filter and sort controls for project list
 * Includes: status filter, sort by, sort order, clear filters
 */

import React from 'react';
import { ProjectStatus, ProjectFilters } from '../../types/project';

interface FilterPanelProps {
  filters: ProjectFilters;
  onChange: (filters: ProjectFilters) => void;
  onClear: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onChange,
  onClear,
}) => {
  const hasActiveFilters =
    filters.status || (filters.sort_by && filters.sort_by !== 'created_at') || filters.sort_order === 'asc';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
      <div className="flex flex-wrap gap-4 items-end">
        {/* Status Filter */}
        <div className="flex-1 min-w-[200px]">
          <label
            htmlFor="status-filter"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Status
          </label>
          <select
            id="status-filter"
            value={filters.status || ''}
            onChange={(e) =>
              onChange({
                ...filters,
                status: (e.target.value as ProjectStatus) || undefined,
              })
            }
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Sort By */}
        <div className="flex-1 min-w-[200px]">
          <label
            htmlFor="sort-by"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Sort By
          </label>
          <select
            id="sort-by"
            value={filters.sort_by || 'created_at'}
            onChange={(e) =>
              onChange({
                ...filters,
                sort_by: e.target.value as ProjectFilters['sort_by'],
              })
            }
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border"
          >
            <option value="created_at">Created Date</option>
            <option value="updated_at">Updated Date</option>
            <option value="name">Name</option>
          </select>
        </div>

        {/* Sort Order */}
        <div className="flex-1 min-w-[150px]">
          <label
            htmlFor="sort-order"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Order
          </label>
          <select
            id="sort-order"
            value={filters.sort_order || 'desc'}
            onChange={(e) =>
              onChange({
                ...filters,
                sort_order: e.target.value as 'asc' | 'desc',
              })
            }
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border"
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div>
            <button
              onClick={onClear}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              aria-label="Clear all filters"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
